import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import img from "../../constant";
import {
  consolePaymentService,
  consoleService,
  subscriptionService,
  onboardingPaymentService,
} from "../../services/api/settings";
import { Frequency, Plan, Provider, Tier } from "../../store/types/settings";
import { toast } from "sonner";
import ModalNew from "../../components/Modal/ModalNew";
import BankTransferModal from "./misc/BankTransferModal";
import { ChevronDown, ChevronUp, CreditCard, Info } from "lucide-react";
import Button2 from "../../components/elements/forms/Button2";

export interface SubscribeNow {
  tier: Tier;
  provider: Provider;
  frequency: Frequency;
  organizationId: string;
  featureIds: string[];
  directFeatureIds: string[];
  directPlan?: {
    tier: "STARTER" | "GROWTH" | "SCALE";
    includeSetupFee: boolean;
  };
}

const NewPaymentSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const tier = searchParams.get("tier") || "";
  const provider = searchParams.get("provider");
  const frequencyParam = searchParams.get("frequency");
  const featureIdsParam = searchParams.get("featureIds");
  const directAddOnIdsParam = searchParams.get("directAddOnIds");
  const directPlanTier = searchParams.get("directPlan");
  const includeSetupFee = searchParams.get("includeSetupFee") === "true";
  const amountParam = searchParams.get("amount");
  const amount = amountParam ? parseFloat(amountParam) : undefined;
  const deploymentIdsParam = searchParams.get("deploymentIds");
  const integrationIdsParam = searchParams.get("integrationIds");
  const planIdParam = searchParams.get("planId");

  const deploymentIds = deploymentIdsParam
    ? deploymentIdsParam.split(",").filter((id) => id.trim() !== "")
    : [];
  const integrationIds = integrationIdsParam
    ? integrationIdsParam.split(",").filter((id) => id.trim() !== "")
    : [];

  const isConsolePlan = deploymentIds.length > 0; // Assumption: Console plans have deployment costs

  const directPlan = directPlanTier
    ? {
        tier: directPlanTier as "STARTER" | "GROWTH" | "SCALE",
        includeSetupFee,
      }
    : undefined;

  const [selectedMethod, setSelectedMethod] = useState<
    "paypal" | "paystack" | "bank-transfer" | null
  >(null);
  const [showInfo, setShowInfo] = useState(false);
  const [open, setOpen] = useState(false);
  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState(false);

  const handleModalClose = () => setOpen(false);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    tier: "",
    amount: "",
    frequency: "MONTHLY" as Frequency,
  });
  const [failedModal, setFailedModal] = useState({
    isOpen: false,
    errorMessage: "",
  });

  // Payment function starts
  const [billingType, setBillingType] = useState<"Monthly" | "Yearly">(
    "Monthly",
  );

  // Parse feature IDs from URL
  const selectedFeatureIds = featureIdsParam
    ? featureIdsParam.split(",").filter((id) => id.trim() !== "")
    : [];
  console.log("Selected Feature IDs:", selectedFeatureIds);
  console.log("Selected Feature IDs Param:", featureIdsParam);
  const selectedDirectAddOnIds = directAddOnIdsParam
    ? directAddOnIdsParam.split(",").filter((id) => id.trim() !== "")
    : [];
  console.log("Selected Direct AddOn IDs:", selectedDirectAddOnIds);
  console.log("Selected Direct AddOn IDs Param:", directAddOnIdsParam);
  const [lastAttemptedPlan, setLastAttemptedPlan] = useState<Plan | null>(null);
  const handleSubscription = async (
    plan: Plan,
    provider: Provider = "paypal",
  ) => {
    // Validate organization ID
    if (!id) {
      toast.error("Organization ID is missing");
      return;
    }

    // Handle VIP/Contact Us plans
    if (plan.isVip || !plan.tier) {
      window.location.href = "mailto:solutions@25thmail.com";
      return;
    }

    const loadingKey = `${plan.tier}-${provider}`;

    // Prevent duplicate requests
    if (loading[loadingKey]) return;

    setLoading((prev) => ({ ...prev, [loadingKey]: true }));
    setLastAttemptedPlan(plan);

    try {
      const frequency: Frequency =
        frequencyParam?.toUpperCase() === "YEARLY" ? "YEARLY" : "MONTHLY";
      // billingType === "Monthly" ? "MONTHLY" : "YEARLY";

      // AI Tiers list for validation
      // AI Tiers list for validation
      // AI Tiers list for validation (removed strict check to allow Direct tiers)
      // const aiTiers = ["PROFESSIONAL", "ENTERPRISE", "ENTERPRISEPLUS"];

      // Determine the tier
      // If tier param is empty, or if we have a directPlan coverage, we should verify intent.
      // But adhering to "tier selected should be used":
      let finalTier = tier;

      // Heuristic: If tier is missing or "PROFESSIONAL" (default) BUT we have a directPlan and NO ai features,
      // user might actually want the Direct Tier as the main tier (Pure Direct Plan).
      // However, we strictly respect the URL param `tier`.
      // If the URL param was fixed in Pricing.tsx, it should be correct (e.g. SCALE).

      console.log("Deriving Tier:", { rawTier: tier, directPlanTier });

      // If somehow tier is empty, fallback to directPlanTier
      if (!finalTier && directPlanTier) {
        finalTier = directPlanTier;
      }

      const aiTier = finalTier.toUpperCase() as Tier;

      console.log("Final Resolved Tier:", aiTier);

      const subscriptionData: SubscribeNow = {
        tier: aiTier,
        provider,
        frequency,
        // For Direct Plans, featureIds should be empty unless explicitly selected (mixed plan)
        featureIds: selectedFeatureIds,
        organizationId: id,
        directFeatureIds: selectedDirectAddOnIds,
        directPlan,
      };

      let result;

      if (isConsolePlan) {
        // Console Payment Flow
        console.log("=== CONSOLE PAYMENT ===");
        console.log("Plan ID:", planIdParam || subscriptionData.tier);
        console.log("Frequency:", frequency);
        console.log("Integration IDs:", integrationIds);
        console.log("Deployment Cost IDs:", deploymentIds);
        console.log("Provider:", provider);
        console.log("Organization ID:", id);

        if (provider === "bank-transfer") {
          const consolePayload = {
            organizationId: id,
            planId: planIdParam || subscriptionData.tier,
            frequency,
            integrationIds,
            deploymentCostIds: deploymentIds,
            provider: provider as any,
          };
          console.log("Console Bank Transfer Payload:", consolePayload);
          result = await consoleService.prepareManualTransfer(consolePayload);
        } else {
          // PayPal or Paystack
          const consolePayload = {
            organizationId: id,
            planId: planIdParam || subscriptionData.tier,
            frequency,
            integrationIds,
            deploymentCostIds: deploymentIds,
            provider,
          };
          console.log("Console Payment Payload:", consolePayload);
          result = await consolePaymentService.plan(consolePayload);
        }
      } else if (directPlan) {
        // Direct Payment Flow
        console.log("=== DIRECT PAYMENT ===");
        console.log("Selected Tier:", subscriptionData.tier);
        console.log("Direct Plan:", directPlan);
        console.log("Provider:", subscriptionData.provider);
        console.log("Frequency:", subscriptionData.frequency);
        console.log("Direct Feature IDs:", selectedDirectAddOnIds);
        console.log("Organization ID:", subscriptionData.organizationId);
        console.log("Amount:", amount);
        console.log("Full Direct Payload:", subscriptionData);

        result = await subscriptionService.subscribe(
          subscriptionData.tier,
          subscriptionData.provider,
          subscriptionData.frequency,
          subscriptionData.organizationId,
          subscriptionData.featureIds.length > 0
            ? subscriptionData.featureIds
            : undefined,
          subscriptionData.directPlan,
          selectedDirectAddOnIds.length > 0
            ? selectedDirectAddOnIds
            : undefined,
          amount,
        );
      } else {
        // AI Recruiter Payment Flow
        console.log("=== AI RECRUITER PAYMENT ===");
        console.log("Selected Tier:", subscriptionData.tier);
        console.log("Provider:", subscriptionData.provider);
        console.log("Frequency:", subscriptionData.frequency);
        console.log("AI Feature IDs:", subscriptionData.featureIds);
        console.log("Organization ID:", subscriptionData.organizationId);
        console.log("Full AI Payload:", subscriptionData);

        result = await subscriptionService.subscribe(
          subscriptionData.tier,
          subscriptionData.provider,
          subscriptionData.frequency,
          subscriptionData.organizationId,
          subscriptionData.featureIds.length > 0
            ? subscriptionData.featureIds
            : undefined,
          subscriptionData.directPlan,
          selectedDirectAddOnIds.length > 0
            ? selectedDirectAddOnIds
            : undefined,
          amount,
        );
      }

      if (
        result.status === "success" &&
        result.data &&
        "authorizationUrl" in result.data
      ) {
        // Open payment gateway in new tab
        window.open((result.data as any).authorizationUrl, "_blank");
        toast.success(`Redirecting to ${provider} payment...`);
        // Show success modal (simulate success for demo - in production, handle via callback)
        // You may want to show this after payment confirmation from gateway
        setTimeout(() => {
          setSuccessModal({
            isOpen: true,
            tier: plan.name,
            amount:
              frequency === "MONTHLY" ? plan.priceMonthly : plan.priceYearly,
            //frequency  === "Monthly" ? plan.priceMonthly : plan.priceYearly,
            frequency,
          });
        }, 2000);
      } else {
        // Show failed modal
        setFailedModal({
          isOpen: true,
          errorMessage: "Unable to initiate payment process. Please try again.",
        });
        toast.error("Unable to initiate payment process");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setFailedModal({
        isOpen: true,
        errorMessage:
          "Something went wrong while processing your request. Please try again.",
      });
      toast.error("Something went wrong while processing your request");
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleRetryPayment = () => {
    if (lastAttemptedPlan) {
      handleSubscription(lastAttemptedPlan);
    }
  };
  // Payment function ends

  // Convert frequency to MONTHLY or YEARLY, default to MONTHLY
  // const frequency: "MONTHLY" | "YEARLY" =
  //   frequencyParam?.toUpperCase() === "YEARLY" ? "YEARLY" : "MONTHLY";

  const handleProceed = async () => {
    if (!selectedMethod) return;

    const reference =
      searchParams.get("paymentReference") ||
      searchParams.get("reference") ||
      searchParams.get("ref");
    if (reference) {
      const loadingKey = "onboarding-payment";
      setLoading((prev) => ({ ...prev, [loadingKey]: true }));
      try {
        if (selectedMethod === "bank-transfer") {
          const result = await onboardingPaymentService.initialize({
            reference,
            provider: "manual",
          });
          if (result.success !== false) {
            navigate(
              `/payment-upload?Id=${id || ""}&ref=${reference}&amount=${amount}`,
            );
          }
        } else {
          const result = await onboardingPaymentService.initialize({
            reference,
            provider: selectedMethod as "paypal" | "paystack",
          });
          if (result.success !== false && result.data?.authorizationUrl) {
            window.open(result.data.authorizationUrl, "_blank");
            toast.success(`Redirecting to ${selectedMethod} payment...`);
            setTimeout(() => {
              setSuccessModal({
                isOpen: true,
                tier: "Onboarding Payment",
                amount: amount ? amount.toString() : "",
                frequency: "MONTHLY",
              });
            }, 2000);
          } else {
            toast.error("Unable to initiate payment process.");
          }
        }
      } catch (error) {
        console.error("Onboarding payment error:", error);
      } finally {
        setLoading((prev) => ({ ...prev, [loadingKey]: false }));
      }
      return;
    }

    // Build the feature IDs parameter
    const featureParam = featureIdsParam
      ? `&featureIds=${featureIdsParam}`
      : "";
    // Build the feature IDs parameter
    // console.log("featureParam", featureParam);

    const directAddOnParam = directAddOnIdsParam
      ? `&directAddOnIds=${directAddOnIdsParam}`
      : "";

    if (selectedMethod === "paypal") {
      // Call subscription API which redirects to PayPal
      handleSubscription(
        {
          tier: tier as Tier,
          priceMonthly: "", // Not needed for API call
          priceYearly: "",
          name: tier,
        } as Plan,
        "paypal",
      );
    } else if (selectedMethod === "paystack") {
      // Call subscription API which redirects to Paystack
      // setOpen(true);
      handleSubscription(
        {
          tier: tier as Tier,
          priceMonthly: "",
          priceYearly: "",
          name: tier,
        } as Plan,
        "paystack",
      );
    } else if (selectedMethod === "bank-transfer") {
      if (isConsolePlan) {
        const loadingKey = "console-bank-transfer";
        setLoading((prev) => ({ ...prev, [loadingKey]: true }));
        try {
          // Register console payment intent for bank transfer
          const frequency: Frequency =
            frequencyParam?.toUpperCase() === "YEARLY" ? "YEARLY" : "MONTHLY";

          // Use prepareManualTransfer for bank-transfer as requested
          await consoleService.prepareManualTransfer({
            organizationId: id || "",
            planId: planIdParam || tier || "",
            frequency,
            integrationIds,
            deploymentCostIds: deploymentIds,
            provider: "bank-transfer" as any,
          });
          setIsBankTransferModalOpen(true);
        } catch (error) {
          console.error("Failed to initiate console bank transfer", error);
          // Toast handled in service, but we might want to still show modal or not?
          // If critical failure, don't show modal.
        } finally {
          setLoading((prev) => ({ ...prev, [loadingKey]: false }));
        }
      } else {
        // Open bank transfer modal
        setIsBankTransferModalOpen(true);
      }
    }
  };

  const paymentOptions = [
    {
      id: "paypal",
      name: "PayPal",
      subtext: "Pay with PayPal",
      icon: img.paypal,
      color: "text-[#0070BA]",
    },
    {
      id: "paystack",
      name: "paystack",
      subtext: "Pay with Paystack",
      icon: img.paystack,
      color: "text-[#00C3F7]",
    },
    {
      id: "bank-transfer",
      name: "Bank Transfer",
      subtext: "Pay with Direct Bank Transfer",
      icon: img.bankTransfer,
      color: "text-[#1976D2]",
    },
  ];
  return (
    <div className="font-abel min-h-screen bg-white flex flex-col items-center w-full h-full py-12">
      {/* Header */}
      <div className="text-center mb-8 px-4">
        <h1 className="text-[32px] font-medium text-[#212121] mb-2">
          Select Payment Method
        </h1>
        <p className="text-gray-500 text-[16px]">
          Choose your preferred payment method
        </p>
      </div>

      {/* Selection Area */}
      <div className="bg-[#F5F5F5] w-full">
        <div className="w-full max-w-[900px] mx-auto p-5 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
            {paymentOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedMethod === option.id;

              return (
                <div
                  key={option.id}
                  onClick={() => setSelectedMethod(option.id as any)}
                  className={`
                  relative 
                  flex flex-col items-center justify-center h-[160px]
                  
                `}
                >
                  {/* Radio Button */}
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <div
                      className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${isSelected ? "border-[#2D6EEF]" : "border-gray-300"}
                  `}
                    >
                      {isSelected && (
                        <div className="w-2.5 h-2.5 rounded-full bg-[#2D6EEF]" />
                      )}
                    </div>
                  </div>

                  <div
                    className={`ml-5 h-[105px] md:w-[200px] flex flex-col items-center border-2 bg-white rounded-[8px] py-4 md:py-7 px-3 md:px-4 cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? "border-[#2D6EEF] shadow-md"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <img src={option.icon} alt="25th" className="" />
                    </div>
                    <p className="text-[12px] text-gray-500 font-medium">
                      {option.subtext}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="w-full max-w-[700px] md:my-8 my-5 px-4">
        <div
          className="bg-[#F2F7FF] border border-gray-200 rounded-[8px] overflow-hidden cursor-pointer"
          onClick={() => setShowInfo(!showInfo)}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-[#2D6EEF] flex items-center justify-center text-white">
                <Info size={16} />
              </div>
              <span className="text-[#212121] font-medium">
                Payment Information
              </span>
            </div>
            {showInfo ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </div>

          {showInfo && (
            <div className="px-4 pb-4 pl-[52px]">
              <ul className="text-gray-600 text-[14px] space-y-2 list-disc">
                <li>All payments are processed securely</li>
                <li>PayPal and Paystack payments are instant</li>
                <li>
                  Bank transfer payments require manual verification (1-2
                  business days)
                </li>
                <li>You will receive a confirmation email after payment</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center gap-4 mt-3">
        {selectedMethod && (
          <Button2
            title={
              loading["console-bank-transfer"]
                ? "Processing..."
                : "Proceed to Payment"
            }
            className="bg-[#2D6EEF] text-white w-[250px] hover:bg-[#1E5DD8] border-none py-2 rounded-[8px]"
            onSubmitHandler={handleProceed}
            disabled={loading["console-bank-transfer"]}
            // onSubmitHandler={
            //   if (provider === "bank-transfer" && onBankTransferClick) {
            //     onBankTransferClick()

            //   } else {
            //     handleSubscription(plan)
            //   }
            // }
          />
        )}

        <button
          onClick={() =>
            navigate(
              `/price?Id=${id}${tier ? `&tier=${tier}` : ""}${directAddOnIdsParam ? `&directAddOnIds=${directAddOnIdsParam}` : ""}`,
            )
          }
          className="border border-[#2D6EEF] text-[#2D6EEF] px-6 py-3 rounded-[8px] text-[14px] hover:text-white hover:bg-[#1E5DD8] transition-colors mt-3"
        >
          Back to Pricing
        </button>
      </div>

      {/* Paystack Modal */}
      <ModalNew
        isOpen={open}
        onClose={handleModalClose}
        title=""
        className="max-w-[300px] mx-auto"
      >
        <div className="text-center">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#00C3F7] to-[#0081CB] flex items-center justify-center mb-4">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-[24px] font-bold mb-2 text-[#212121]">
              Paystack
            </h3>
            <p className="text-gray-600 text-[18px] mb-4">Coming Soon</p>
          </div>
        </div>
      </ModalNew>

      {/* Bank Transfer Modal */}
      <BankTransferModal
        isOpen={isBankTransferModalOpen}
        onClose={() => setIsBankTransferModalOpen(false)}
        organizationId={id || ""}
        tier={tier as Tier}
        frequency={
          frequencyParam?.toUpperCase() === "YEARLY" ? "YEARLY" : "MONTHLY"
        }
        // distinct featureIds logic: SEPARATE them!
        featureIds={selectedFeatureIds}
        directAddOnIds={selectedDirectAddOnIds}
        directPlan={directPlan}
        amount={amount}
        deploymentIds={deploymentIds}
        integrationIds={integrationIds}
        isConsolePlan={isConsolePlan}
        planId={planIdParam || tier}
      />
    </div>
  );
};

export default NewPaymentSelection;
